import { useContext } from 'react';

import { useDetectUserSessionQuery } from '../../graphql/generated';
import type { GlobalStorageContextProps } from '../../providers/GlobalStorage';
import { globalStorageContext } from '../../providers/GlobalStorage';
import { useEffectOnMount } from '../useEffectOnMount';

export const useGlobalStorageContext = (): GlobalStorageContextProps => {
  const context = useContext(globalStorageContext);
  return context;
};

/**
 * Clear the global state we use to preserve the image added during the
 * homeowner app upload process that was interrupted by the authentication flow
 * (onboarding) to prevent subsequent authentications from directing the user to
 * the upload loading screen for a "stale" upload.
 *
 * @see {@link https://github.com/hoverinc/web-react/pull/5071}
 * @see {@link https://hoverinc.atlassian.net/browse/MEOW-4008}
 *
 * @todo we probably be using URL state, likely in combination with the
 * `?redirect_to` param, instead of relying on global application state to pass
 * the image through the onboarding flow and overloading the `/designer` route
 * to handle uploads that were initiated before authentication. With the current
 * global state implementation, it's possible there are other edge cases that
 * would result in directing the user to the loading screen when we shouldn't be
 * if this state isn't cleared correctly.
 */
export const useClearGlobalStorageUpload = () => {
  const { data } = useDetectUserSessionQuery({ ssr: false });
  const {
    setHomeownerAppUploadFile,
    setHomeownerAppSpaceType,
    homeownerAppUploadFile,
  } = useGlobalStorageContext();

  const isLoggedIn = !!data?.profile.user?.id;

  useEffectOnMount(() => {
    if (homeownerAppUploadFile && !isLoggedIn) {
      setHomeownerAppUploadFile();
      setHomeownerAppSpaceType();
    }
  });
};
